import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getHotArticles, getNewArticles, articles } from '@/mocks/articles';

const heroImage = "https://readdy.ai/api/search-image?width=1800&height=900&seq=sigua-hero-2024-v1&orientation=landscape&query=Abstract%20cinematic%20composition%20with%20warm%20amber%20and%20deep%20crimson%20gradients%2C%20geometric%20film%20strip%20silhouettes%20floating%20in%20layered%20space%2C%20soft%20bokeh%20light%20particles%2C%20modern%20streaming%20aesthetic%2C%20dark%20theater%20curtain%20textures%20blending%20with%20digital%20patterns%2C%20atmospheric%20and%00elegant%2C%20no%20text%20no%20people%2C%20artistic%20minimal%20design";

export default function Home() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const hotArticles = getHotArticles(8);
  const newArticles = getNewArticles(8);

  const filteredArticles = selectedCategory === 'all'
    ? articles
    : articles.filter((a) => a.category === selectedCategory);

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '丝瓜视频',
        url: 'https://sigua.video',
        description: '聚合51视频、91视频等平台最新影视资源，热门电视剧、电影、综艺、动漫免费在线追剧，高清不缓冲。',
        sameAs: ['https://weibo.com/sigua', 'https://bilibili.com/sigua'],
      },
      {
        '@type': 'WebSite',
        name: '丝瓜视频',
        url: 'https://sigua.video',
        potentialAction: {
          '@type': 'SearchAction',
          'target': 'https://sigua.video/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '丝瓜视频 - 最新影视在线追剧平台',
        description: '丝瓜视频聚合51视频、91视频等平台最新影视资源，新剧上线第一时间更新，热门电视剧、电影、综艺免费在线追剧，高清不缓冲。',
        url: 'https://sigua.video',
        isPartOf: { '@id': 'https://sigua.video/#website' },
        about: { '@id': 'https://sigua.video/#organization' },
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '丝瓜视频是否需要付费观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '丝瓜视频所有影视资源均免费在线观看，无需付费。我们聚合51视频、91视频等平台的公开资源，为用户提供便捷的追剧体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何快速找到我想看的电视剧或电影？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的分类导航（电视剧、电影、综艺、动漫、纪录片）快速浏览对应类型的内容，也可以使用搜索功能直接查找您喜欢的影视作品。',
        },
      },
      {
        '@type': 'Question',
        name: '新剧上线后多久可以在丝瓜视频观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '丝瓜视频与51视频、91视频等平台保持紧密的更新同步，新剧上线后我们会在第一时间更新资源，通常当天即可在线观看。',
        },
      },
      {
        '@type': 'Question',
        name: '观看视频需要安装什么软件或插件吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要安装任何软件或插件。丝瓜视频支持主流浏览器直接在线播放，包括Chrome、Safari、Edge等，高清流畅不缓冲。',
        },
      },
      {
        '@type': 'Question',
        name: '丝瓜视频支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '丝瓜视频全面支持PC端、平板电脑和手机端观看。无论在电脑前还是移动设备上，都能享受高清流畅的追剧体验。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src={heroImage}
            alt="丝瓜视频 - 最新影视在线追剧平台 高清不缓冲免费观看"
            title="丝瓜视频 - 最新影视在线追剧平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <div className="w-full max-w-3xl">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-background-50 mb-4 md:mb-6 leading-tight">
                最新影视 <strong className="text-primary-400">免费在线追剧</strong>
              </h1>
              <p className="text-base md:text-lg text-background-50/80 max-w-xl mx-auto mb-6 md:mb-8 leading-relaxed">
                聚合<strong>51视频</strong>、<strong>91视频</strong>等平台最新影视资源，新剧上线第一时间更新，高清不缓冲
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/dianshiju"
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm md:text-base hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line mr-2"></i>开始追剧
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 rounded-full font-medium text-sm md:text-base hover:bg-background-50/30 transition-all duration-300 whitespace-nowrap cursor-pointer border border-background-50/30"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center gap-3 md:gap-4 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 hover:bg-primary-100 text-foreground-700 hover:text-primary-700 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border border-background-200/70 hover:border-primary-200"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                  <span className="text-xs text-foreground-400 ml-1">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门推荐
                </h2>
                <p className="text-foreground-500 text-sm mt-1">全网最热影视内容，大家都在追</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {hotArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  <i className="ri-flashlight-line text-accent-500 mr-2"></i>
                  最新更新
                </h2>
                <p className="text-foreground-500 text-sm mt-1">新剧上线，第一时间更新</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {newArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                全部影视
              </h2>
              <p className="text-foreground-500 text-sm">按分类浏览所有内容</p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedCategory === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-200/70 text-foreground-600 hover:bg-background-300'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200/70 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredArticles.slice(0, 8).map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                为什么选择<strong>丝瓜视频</strong>
              </h2>
              <p className="text-foreground-500 text-sm">聚合51视频、91视频等优质平台资源，打造极致追剧体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-flashlight-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-950 mb-2">极速更新</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">新剧上线第一时间同步更新，让你快人一步追到最新剧集</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-hd-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-950 mb-2">高清画质</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">支持高清流畅播放，不卡顿不缓冲，给你沉浸式观影体验</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-global-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-950 mb-2">海量资源</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">聚合51视频、91视频等多平台资源，电视剧电影综艺动漫应有尽有</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-device-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-950 mb-2">多端适配</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">电脑、平板、手机全终端适配，随时随地畅享追剧乐趣</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm">关于丝瓜视频的常见疑问解答</p>
            </div>
            <div className="space-y-3">
              {faqSchema.mainEntity.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-foreground-800 text-sm hover:text-primary-600 transition-colors">
                    {faq.name}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.acceptedAnswer.text}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}

function ArticleCard({ article }: { article: typeof articles[0] }) {
  const categoryInfo = categories.find((c) => c.slug === article.category);
  const cardImage = getArticleImage(article);

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={cardImage}
          alt={`${article.title} - ${article.description.slice(0, 30)}`}
          title={`${article.title} - 丝瓜视频在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2 flex gap-1.5">
          {article.isHot && (
            <span className="px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
              热门
            </span>
          )}
          {article.isNew && (
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
              新上线
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-background-50 text-xs font-medium whitespace-nowrap">
          <i className="ri-star-fill text-accent-400 mr-0.5 text-[10px]"></i>
          {article.rating}
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="font-heading text-sm md:text-base text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1 mb-1">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-2">
          {article.description}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-400">
            {categoryInfo ? (
              <>
                <i className={`${categoryInfo.icon} mr-1`}></i>
                {categoryInfo.name}
              </>
            ) : (
              article.episodeCount
            )}
          </span>
          <span className="text-xs text-foreground-400">{article.year}</span>
        </div>
      </div>
    </Link>
  );
}

function getArticleImage(article: typeof articles[0]): string {
  const imageMap: Record<string, string> = {
    'article-changfengdu': "https://readdy.ai/api/search-image?query=Ancient%20Chinese%20drama%20scene%20with%20elegant%20hanfu%20costumes%2C%20warm%20lantern%20light%2C%20traditional%20architecture%20background%2C%20soft%20romantic%20atmosphere%2C%20period%20drama%20aesthetic%2C%20cinematic%20lighting%2C%20minimal%20composition&width=800&height=450&seq=article-changfengdu&orientation=landscape",
    'article-kuangbiao': "https://readdy.ai/api/search-image?query=Dark%20crime%20drama%20scene%20with%20police%20officer%20silhouette%20against%20city%20lights%2C%20noir%20atmosphere%2C%20rainy%20street%20at%20night%2C%20tense%20moody%20lighting%2C%20Chinese%20urban%20landscape%2C%20cinematic%20composition&width=800&height=450&seq=article-kuangbiao&orientation=landscape",
    'article-langyabang2': "https://readdy.ai/api/search-image?query=Epic%20Chinese%20historical%20drama%20scene%20with%20ancient%20battlefield%2C%20soldiers%20in%20armor%2C%20dramatic%20sky%20at%20sunset%2C%20traditional%20Chinese%20military%20banners%2C%20cinematic%20widescreen%20composition%2C%20warm%20golden%20light&width=800&height=450&seq=article-langyabang2&orientation=landscape",
    'article-liulangdiqiu2': "https://readdy.ai/api/search-image?query=Futuristic%20sci-fi%20scene%20with%20massive%20space%20station%20and%20Earth%20in%20background%2C%20dramatic%20cosmic%20lighting%2C%20advanced%20technology%20structures%2C%20cinematic%20sci-fi%20aesthetic%2C%20deep%20space%20atmosphere%2C%20blue%20engine%20glow&width=800&height=450&seq=article-liulangdiqiu2&orientation=landscape",
    'article-manjianghong': "https://readdy.ai/api/search-image?query=Ancient%20Chinese%20courtyard%20at%20night%20with%20red%20lanterns%2C%20traditional%20architecture%2C%20mysterious%20shadowy%20figures%2C%20suspenseful%20atmosphere%2C%20warm%20amber%20lighting%2C%20cinematic%20noir%20style%2C%20period%20drama%20aesthetic&width=800&height=450&seq=article-manjianghong&orientation=landscape",
    'article-guzhuyizhi': "https://readdy.ai/api/search-image?query=Gritty%20Southeast%20Asian%20street%20scene%20with%20neon%20signs%2C%20dark%20alley%20with%20industrial%20buildings%2C%20tense%20atmosphere%2C%20dim%20yellow%20lighting%2C%20crime%20thriller%20aesthetic%2C%20cinematic%20composition&width=800&height=450&seq=article-guzhuyizhi&orientation=landscape",
    'article-fengshen1': "https://readdy.ai/api/search-image?query=Epic%20Chinese%20mythological%20scene%20with%20ancient%20palace%20and%20warriors%2C%20dramatic%20storm%20clouds%2C%20golden%20divine%20light%20breaking%20through%2C%20grand%20scale%20fantasy%20aesthetic%2C%20cinematic%20widescreen%20composition&width=800&height=450&seq=article-fengshen1&orientation=landscape",
    'article-chengfengpolang': "https://readdy.ai/api/search-image?query=Dynamic%20stage%20performance%20with%20colorful%20concert%20lighting%2C%20spotlights%20and%20fog%20effects%2C%20music%20show%20atmosphere%2C%20elegant%20dancers%20in%20motion%2C%20vibrant%20energetic%20scene%2C%20entertainment%20show%20aesthetic&width=800&height=450&seq=article-chengfengpolang&orientation=landscape",
    'article-tuokouxiu': "https://readdy.ai/api/search-image?query=Stand-up%20comedy%20stage%20with%20warm%20spotlight%2C%20microphone%20stand%20on%20stage%2C%20red%20curtain%20background%2C%20intimate%20theater%20atmosphere%2C%20brick%20wall%20texture%2C%20comedic%20club%20aesthetic&width=800&height=450&seq=article-tuokouxiu&orientation=landscape",
    'article-benghuai': "https://readdy.ai/api/search-image?query=Futuristic%20anime%20style%20sci-fi%20train%20traveling%20through%20colorful%20galaxy%2C%20vibrant%20nebula%20clouds%2C%20starry%20space%20background%2C%20dynamic%20composition%2C%20Japanese%20anime%20aesthetic%2C%20bright%20energetic%20colors&width=800&height=450&seq=article-benghuai&orientation=landscape",
    'article-douluodalu': "https://readdy.ai/api/search-image?query=Chinese%20anime%20style%20fantasy%20battle%20scene%2C%20martial%20arts%20warrior%20with%20glowing%20energy%2C%20ancient%20Chinese%20landscape%20with%20mountains%2C%20dynamic%20action%20pose%2C%20vibrant%20magical%20effects%2C%20anime%20aesthetic&width=800&height=450&seq=article-douluodalu&orientation=landscape",
    'article-zhuxian': "https://readdy.ai/api/search-image?query=Chinese%20xianxia%20style%20fantasy%20scene%20with%20floating%20mountains%20and%20ancient%20temple%2C%20misty%20atmosphere%2C%20swordsman%20in%20traditional%20robes%2C%20ethereal%20lighting%2C%20dreamy%20composition%2C%20wuxia%20aesthetic&width=800&height=450&seq=article-zhuxian&orientation=landscape",
    'article-shejianshangdezhongguo': "https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20cuisine%20close-up%20on%20wooden%20table%2C%20steaming%20hot%20traditional%20dishes%2C%20warm%20natural%20lighting%2C%20rustic%20kitchen%20background%2C%20food%20documentary%20style%2C%20appetizing%20composition&width=800&height=450&seq=article-shejianshangdezhongguo&orientation=landscape",
    'article-lansexingqiu': "https://readdy.ai/api/search-image?query=Underwater%20ocean%20scene%20with%20colorful%20coral%20reef%20and%20tropical%20fish%2C%20sunlight%20rays%20piercing%20through%20water%20surface%2C%20deep%20blue%20ocean%20background%2C%20nature%20documentary%20style%2C%20pristine%20marine%20ecosystem&width=800&height=450&seq=article-lansexingqiu&orientation=landscape",
    'article-hexizoulang': "https://readdy.ai/api/search-image?query=Ancient%20Silk%20Road%20landscape%20with%20sand%20dunes%20and%20camel%20caravan%2C%20golden%20sunset%20light%2C%20vast%20desert%20panorama%2C%20historical%20trading%20route%20aesthetic%2C%20dramatic%20sky%20with%20warm%20colors%2C%20epic%20scale&width=800&height=450&seq=article-hexizoulang&orientation=landscape",
    'article-santi': "https://readdy.ai/api/search-image?query=Mysterious%20sci-fi%20scene%20with%20three%20suns%20in%20alien%20sky%2C%20strange%20landscape%20with%20unusual%20vegetation%2C%20cosmic%20atmosphere%2C%20hard%20science%20fiction%20aesthetic%2C%20dramatic%20lighting%2C%20cinematic%20composition&width=800&height=450&seq=article-santi&orientation=landscape",
    'article-qingyunian2': "https://readdy.ai/api/search-image?query=Ancient%20Chinese%20imperial%20palace%20interior%20with%20ornate%20details%2C%20warm%20candlelight%2C%20traditional%20furniture%2C%20elegant%20court%20atmosphere%2C%20period%20drama%20aesthetic%2C%20cinematic%20depth&width=800&height=450&seq=article-qingyunian2&orientation=landscape",
    'article-xiaoshideta': "https://readdy.ai/api/search-image?query=Tropical%20island%20resort%20at%20twilight%20with%20palm%20trees%2C%20luxurious%20villa%20with%20pool%2C%20mysterious%20atmosphere%2C%20suspense%20thriller%20aesthetic%2C%20warm%20sunset%20colors%2C%20cinematic%20composition&width=800&height=450&seq=article-xiaoshideta&orientation=landscape",
    'article-xihuanni': "https://readdy.ai/api/search-image?query=Cozy%20modern%20living%20room%20with%20warm%20lighting%2C%20comfortable%20sofa%20and%20decorative%20plants%2C%20romantic%20atmosphere%2C%20dating%20show%20aesthetic%2C%20soft%20natural%20light%20from%20windows%2C%20intimate%20setting&width=800&height=450&seq=article-xihuanni&orientation=landscape",
    'article-quanzhigaoshou': "https://readdy.ai/api/search-image?query=Esports%20gaming%20arena%20with%20colorful%20RGB%20lighting%2C%20professional%20gaming%20setup%20with%20multiple%20monitors%2C%20competitive%20gaming%20atmosphere%2C%20cyberpunk%20aesthetic%2C%20dynamic%20energy%2C%20anime%20style&width=800&height=450&seq=article-quanzhigaoshou&orientation=landscape",
  };
  return imageMap[article.imageSeq] || imageMap['article-changfengdu'];
}